/**
 * API service for communicating with the Asset Classification Server (FastAPI).
 * Handles login, model loading, and reclassification via OpenAI.
 */

const RECLASS_SERVER = process.env.REACT_APP_RECLASS_SERVER_URL || 'http://localhost:8000';

/**
 * Create a reclassification session using an existing AssetPro token.
 * Reuses the token from the main AIM login — no second login needed.
 * @returns {{ session_id, org_name, org_type }}
 */
export async function reclassLoginWithToken(token, organisation) {
  const resp = await fetch(`${RECLASS_SERVER}/reclassify/login-with-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, organisation }),
  });
  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data.detail || data.error || 'Session creation failed');
  }
  return data;
}

/**
 * Fetch all unique asset models for the logged-in organisation.
 * @returns {{ models: Array }}
 */
export async function reclassLoadModels(sessionId) {
  const resp = await fetch(`${RECLASS_SERVER}/reclassify/models?session_id=${sessionId}`);
  const data = await resp.json();
  if (!resp.ok) {
    if (resp.status === 401) throw new Error('Session expired');
    throw new Error(data.error || 'Failed to load models');
  }
  return data;
}

/**
 * Run combined taxonomy + classification in a single OpenAI call.
 * Optionally accepts a `models` array — when provided the server classifies
 * these models instead of its own internally-loaded list, ensuring parity
 * between UI and server.
 * @returns {{ engine, taxonomy, models, total_time_ms, start_time, end_time, processed, errors, stopped }}
 */
export async function reclassProcessCombined(sessionId, engine = 'openai', models = null) {
  const body = { session_id: sessionId, engine };
  if (models) body.models = models;
  const resp = await fetch(`${RECLASS_SERVER}/reclassify/process-combined`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data.detail || data.error || 'Classification failed');
  }
  return data;
}

/**
 * Stop an in-progress reclassification after the current batch.
 */
export async function reclassStop(sessionId) {
  const resp = await fetch(`${RECLASS_SERVER}/reclassify/stop`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
  });
  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data.error || 'Stop request failed');
  }
  return data;
}
